import * as XLSX from 'xlsx';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InventoryJsonRow {
  partNumber: string;
  quantityOnHand: number;
  warehouse?: string;
  bin?: string;
}

export interface POJsonRow {
  poNumber: string;
  partNumber: string;
  vendorId?: string;
  quantityOrdered: number;
  expectedDate: string;
  status: string;
  vendorName?: string;
}

export interface SalesOrderJsonRow {
  soNumber: string;
  customerName: string;
  promisedShipDate: string;
  partNumber: string;
  quantity: number;
}

interface ParseInventoryResult {
  rows: InventoryJsonRow[];
  totalRawRows: number;
  warnings: string[];
}

interface ParsePOResult {
  rows: POJsonRow[];
  warnings: string[];
}

interface ParseSalesOrderResult {
  rows: SalesOrderJsonRow[];
  warnings: string[];
}

export interface GPWorkbookResult {
  inventory?: ParseInventoryResult;
  purchaseOrders?: ParsePOResult;
  salesOrders?: ParseSalesOrderResult;
  sheetsFound: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Strips invisible characters, control codes, BOM markers, and zero-width
 * spaces that frequently appear in GP Excel exports. Non-breaking spaces are
 * converted to regular spaces. The result is trimmed.
 */
export function sanitizeGPString(value: unknown): string {
  if (value == null) return '';
  return String(value)
    .replace(/[\x00-\x1F\x7F]/g, '')      // Control characters
    .replace(/[\u200B-\u200F]/g, '')        // Zero-width spaces, direction marks
    .replace(/[\u2028\u2029]/g, '')         // Line/paragraph separators
    .replace(/[\uFEFF]/g, '')               // BOM / zero-width no-break space
    .replace(/\u00A0/g, ' ')               // Non-breaking space -> regular space
    .trim();
}

/**
 * Convert a value to a number, returning 0 for anything non-numeric.
 * GP exports often have blank cells for zero-quantity rows, so defaulting
 * to 0 is intentional here rather than throwing on non-numeric values.
 */
function toNumber(value: unknown): number {
  if (value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Format a Date (or Excel date serial) as YYYY-MM-DD. Returns '' on failure. */
function toDateString(value: unknown): string {
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof value === 'number') {
    // Excel date serial — convert via SheetJS utility
    const date = XLSX.SSF.parse_date_code(value);
    if (date) {
      const y = date.y;
      const m = String(date.m).padStart(2, '0');
      const d = String(date.d).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  if (typeof value === 'string' && value.trim()) {
    // Already a date string — try to parse and normalise
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return toDateString(parsed);
    }
  }
  return '';
}

/** Read a File into an ArrayBuffer. */
function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

// ---------------------------------------------------------------------------
// Internal parsers (operate on an already-loaded workbook)
// ---------------------------------------------------------------------------

function parseInventoryFromWorkbook(
  workbook: XLSX.WorkBook,
): ParseInventoryResult | null {
  const sheet = workbook.Sheets['InventoryTX&Wheeling'];
  if (!sheet) return null;

  const rawRows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, {
    defval: '',
  });
  const totalRawRows = rawRows.length;
  const warnings: string[] = [];

  // Build bin lookup from LocationBIN sheet if present
  const binMap = new Map<string, string>();
  const binSheet = workbook.Sheets['LocationBIN'];
  if (binSheet) {
    const binRows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(binSheet, {
      defval: '',
    });
    for (const row of binRows) {
      const pn = sanitizeGPString(row['ITEMNMBR']);
      const loc = sanitizeGPString(row['LOCNCODE']);
      const bin = sanitizeGPString(row['BIN']);
      if (pn && loc && bin) {
        binMap.set(`${pn}|${loc}`, bin);
      }
    }
  }

  const rows: InventoryJsonRow[] = [];
  for (let i = 0; i < rawRows.length; i++) {
    const raw = rawRows[i]!;
    const partNumber = sanitizeGPString(raw['ITEMNMBR']);
    if (!partNumber) {
      warnings.push(`Row ${i + 2}: missing ITEMNMBR, skipped`);
      continue;
    }

    const quantityOnHand = toNumber(raw['QTYONHND']);
    const warehouse = sanitizeGPString(raw['LOCNCODE']) || undefined;

    const row: InventoryJsonRow = { partNumber, quantityOnHand };
    if (warehouse) row.warehouse = warehouse;

    const binKey = `${partNumber}|${warehouse ?? ''}`;
    const bin = binMap.get(binKey);
    if (bin) row.bin = bin;

    rows.push(row);
  }

  return { rows, totalRawRows, warnings };
}

function parsePOFromWorkbook(
  workbook: XLSX.WorkBook,
): ParsePOResult | null {
  const sheet = workbook.Sheets['POLine'];
  if (!sheet) return null;

  const rawRows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, {
    defval: '',
  });
  const warnings: string[] = [];

  const rows: POJsonRow[] = [];
  for (let i = 0; i < rawRows.length; i++) {
    const raw = rawRows[i]!;
    const poNumber = sanitizeGPString(raw['PONUMBER']);
    const partNumber = sanitizeGPString(raw['ITEMNMBR']);

    if (!poNumber) {
      warnings.push(`Row ${i + 2}: missing PONUMBER, skipped`);
      continue;
    }
    if (!partNumber) {
      warnings.push(`Row ${i + 2}: missing ITEMNMBR, skipped`);
      continue;
    }

    const vendorId = sanitizeGPString(raw['VENDORID']) || undefined;
    const quantityOrdered = toNumber(raw['QTYORDER']);
    const expectedDate = toDateString(raw['PRMSHPDTE']);
    const status = sanitizeGPString(raw['POLNESTA']);
    const vendorName = sanitizeGPString(raw['VENDNAME']) || undefined;

    if (!expectedDate) {
      warnings.push(`Row ${i + 2}: could not parse PRMSHPDTE date`);
    }

    rows.push({ poNumber, partNumber, vendorId, quantityOrdered, expectedDate, status, vendorName });
  }

  return { rows, warnings };
}

/**
 * GP SalesLine column mapping:
 *   SOPNUMBE   -> soNumber
 *   ShipToName -> customerName
 *   ReqShipDate -> promisedShipDate
 *   ITEMNMBR   -> partNumber
 *   QUANTITY   -> quantity
 */
function parseSalesOrderFromWorkbook(
  workbook: XLSX.WorkBook,
): ParseSalesOrderResult | null {
  const sheet = workbook.Sheets['SalesLine'];
  if (!sheet) return null;

  const rawRows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, {
    defval: '',
  });
  const warnings: string[] = [];

  const rows: SalesOrderJsonRow[] = [];
  for (let i = 0; i < rawRows.length; i++) {
    const raw = rawRows[i]!;
    const soNumber = sanitizeGPString(raw['SOPNUMBE']);
    const partNumber = sanitizeGPString(raw['ITEMNMBR']);

    if (!soNumber) {
      warnings.push(`Row ${i + 2}: missing SOPNUMBE, skipped`);
      continue;
    }
    if (!partNumber) {
      // Blank/header rows in GP export — skip silently
      continue;
    }

    const quantity = toNumber(raw['QUANTITY']);
    if (quantity <= 0) continue;

    const customerName = sanitizeGPString(raw['ShipToName']);
    const promisedShipDate = toDateString(raw['ReqShipDate']);

    // GP uses 1899-12-30/31 as sentinel for "no date"
    if (!promisedShipDate || promisedShipDate.startsWith('1899')) {
      warnings.push(`Row ${i + 2}: no valid ship date for ${soNumber}`);
    }

    rows.push({
      soNumber,
      customerName,
      promisedShipDate: promisedShipDate.startsWith('1899') ? '' : promisedShipDate,
      partNumber,
      quantity,
    });
  }

  return { rows, warnings };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Parse a full GP workbook. Auto-detects which sheets are present and parses
 * all supported ones (SalesLine, POLine, InventoryTX&Wheeling).
 */
export async function parseGPWorkbook(file: File): Promise<GPWorkbookResult> {
  const buffer = await readFileAsArrayBuffer(file);
  const workbook = XLSX.read(buffer, { cellDates: true });

  const sheetsFound = workbook.SheetNames;

  const inventory = parseInventoryFromWorkbook(workbook);
  const purchaseOrders = parsePOFromWorkbook(workbook);
  const salesOrders = parseSalesOrderFromWorkbook(workbook);

  return {
    inventory: inventory ?? undefined,
    purchaseOrders: purchaseOrders ?? undefined,
    salesOrders: salesOrders ?? undefined,
    sheetsFound,
  };
}

/** Parse only the inventory sheet. */
export async function parseInventorySheet(file: File): Promise<ParseInventoryResult> {
  const buffer = await readFileAsArrayBuffer(file);
  const workbook = XLSX.read(buffer, { cellDates: true });
  const result = parseInventoryFromWorkbook(workbook);
  if (!result) {
    throw new Error(
      `Sheet "InventoryTX&Wheeling" not found. Available: ${workbook.SheetNames.join(', ')}`,
    );
  }
  return result;
}

/** Parse only the PO sheet. */
export async function parsePOSheet(file: File): Promise<ParsePOResult> {
  const buffer = await readFileAsArrayBuffer(file);
  const workbook = XLSX.read(buffer, { cellDates: true });
  const result = parsePOFromWorkbook(workbook);
  if (!result) {
    throw new Error(
      `Sheet "POLine" not found. Available: ${workbook.SheetNames.join(', ')}`,
    );
  }
  return result;
}
